<script setup>
import { computed } from 'vue'
import { Link } from '@inertiajs/vue3'
import { BadgeCheck, Video, Users, BookOpen, UserRound } from 'lucide-vue-next'
import { formatMoney } from '@/lib/money'

/**
 * The tutor card — the most important component on the site, because it is the
 * proof. Order is deliberate: face, name, where they studied, what they teach,
 * then price. A parent decides on the first three.
 *
 * The whole card is one link. It used to hold a "View plans" button inside the
 * anchor, which nests one interactive element in another: the button is
 * unreachable by keyboard as a separate target, and Lucky Orange showed people
 * clicking parts of a card expecting different destinations.
 *
 * Avatars fall back to initials drawn locally. The previous fallback called
 * ui-avatars.com on every card, which leaked the tutor's name to a third party
 * and put a network request in front of the most important content on the page.
 */
const props = defineProps({
  tutor: { type: Object, required: true },
  viewMode: {
    type: String,
    default: 'grid',
    validator: (value) => ['grid', 'list'].includes(value),
  },
  linkPrefix: { type: String, default: '/tutors' },
})

const href = computed(() => `${props.linkPrefix}/${props.tutor.slug}`)

const initials = computed(() => {
  const parts = (props.tutor.name || '').split(' ').filter(Boolean)
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
  }
  return (parts[0]?.[0] || '?').toUpperCase()
})

/** The institution line only appears when the tutor recorded one. */
const institution = computed(() => {
  const value = (props.tutor.qualification || '').trim()
  return value && value.toLowerCase() !== 'qualified tutor' ? value : null
})

const SUBJECTS_SHOWN = 3
const LEVELS_SHOWN = 3

const displaySubjects = computed(() => (props.tutor.subjects || []).slice(0, SUBJECTS_SHOWN))
const hiddenSubjectsCount = computed(() => Math.max((props.tutor.subjects || []).length - SUBJECTS_SHOWN, 0))
/**
 * The levels this tutor teaches, which is what a parent is scanning for.
 *
 * This row used to lead with the tutor's own best A-level grade, written as
 * "A* at A-Level" and styled exactly like the levels beside it — so a badge
 * saying what the tutor scored sat in a row of badges saying what they teach,
 * and read as a third level. Their grades are on the profile, under their
 * qualifications, where they mean what they say.
 */
const displayLevels = computed(() => (props.tutor.levels || []).filter((l) => l.name).slice(0, LEVELS_SHOWN))

/**
 * The concept card reads "Saanchi / Economics Tutor / London School of
 * Economics / A* at A-Level" — one subject in the role line, the rest demoted
 * to a footnote. Four subject chips of equal weight told a parent nothing.
 */
const primarySubject = computed(() => props.tutor.subjects?.[0]?.name ?? null)

const otherSubjects = computed(() =>
  (props.tutor.subjects || []).slice(1, 4).map((subject) => subject.name)
)

/**
 * The price of a weekly one-to-one lesson, and nothing else.
 *
 * It used to quote the cheapest of the base and gold plans. Base is the content
 * library — a tutor charging £4 a week for their videos and £40 for lessons was
 * advertised at £4, next to a card that is otherwise entirely about tutoring.
 * Where a tutor offers no one-to-one, no price is shown at all rather than a
 * figure for something else. Silver stays out regardless: group tuition is
 * withdrawn while Bindle Live is built.
 */
const oneToOnePrice = computed(() => {
  const price = Number(props.tutor.price_gold)

  return Number.isFinite(price) && price > 0 ? price : null
})

const serviceConfig = {
  one_on_one: { key: 'one_on_one', label: '1:1 lessons', icon: UserRound },
  videos: { key: 'videos', label: 'Video content', icon: Video },
  materials: { key: 'materials', label: 'Study material', icon: BookOpen },
  group_sessions: { key: 'group_sessions', label: 'Group lessons', icon: Users },
}

/**
 * Only what can actually be bought today, which is one-to-one.
 *
 * Group lessons are withdrawn while Bindle Live is built. Video content and
 * study material went the same way for a sharper reason: the badge came from a
 * teaching method a tutor ticked during onboarding, not from anything they had
 * published, so five tutors were advertising a library that does not exist. The
 * material a tutor does publish is included in both plans and shown on the
 * profile's Content tab, which is evidence rather than a claim.
 */
const WITHDRAWN_SERVICES = ['group_sessions', 'videos', 'materials']

const offeredServices = computed(() =>
  (props.tutor.services_offered || [])
    .filter((key) => !WITHDRAWN_SERVICES.includes(key))
    .map((key) => serviceConfig[key])
    .filter(Boolean)
)
</script>

<template>
  <!-- ============================ GRID ============================ -->
  <Link
    v-if="viewMode === 'grid'"
    :href="href"
    class="group flex h-full flex-col overflow-hidden rounded-xl border border-line bg-surface shadow-xs transition duration-200 ease-brand hover:-translate-y-0.5 hover:border-line-brand hover:shadow-md"
  >
    <!-- Photograph panel, 4:3, as in the concept. The portrait fills it where
         there is one; initials sit on the brand tint where there is not. -->
    <div class="relative aspect-[4/3] overflow-hidden bg-gradient-to-br from-surface-2 to-brand-soft">
      <img
        v-if="tutor.avatar"
        :src="tutor.avatar"
        :alt="tutor.name"
        loading="lazy"
        class="size-full object-cover object-top transition-transform duration-500 ease-brand group-hover:scale-[1.04]"
      />
      <span
        v-else
        class="grid size-full place-items-center font-display text-4xl font-bold text-brand/45"
        aria-hidden="true"
      >{{ initials }}</span>

      <span
        v-if="tutor.is_verified"
        class="absolute right-3 top-3 inline-flex items-center gap-1 rounded-full bg-surface/95 px-2.5 py-1 text-[11px] font-bold text-brand shadow-xs backdrop-blur-sm"
      >
        <BadgeCheck class="size-3.5" /> Verified
      </span>
    </div>

    <div class="flex flex-1 flex-col gap-1 p-5">
      <h3 class="font-display text-lg font-bold leading-tight text-ink">{{ tutor.name }}</h3>
      <p v-if="primarySubject" class="text-sm text-muted">{{ primarySubject }} Tutor</p>

      <!-- The university is the proof, so it is given real weight and its own
           line rather than being one badge among several. -->
      <p v-if="institution" class="mt-3 text-[15px] font-bold leading-snug text-ink">{{ institution }}</p>

      <div class="mt-3 flex flex-wrap gap-1.5">
        <span
          v-for="level in displayLevels"
          :key="level.id"
          class="rounded-sm border border-line-brand bg-brand-soft px-2.5 py-1 text-xs font-semibold text-brand-soft-text"
        >{{ level.name }}</span>
      </div>

      <div class="mt-auto flex items-end justify-between gap-3 border-t border-line pt-4">
        <div v-if="otherSubjects.length" class="min-w-0 text-xs leading-snug text-subtle">
          Also teaches {{ otherSubjects.join(', ') }}
        </div>
        <p v-if="oneToOnePrice" class="ml-auto shrink-0 text-right">
          <span class="block text-[11px] text-subtle">1:1 lesson</span>
          <span class="font-display text-xl font-bold text-ink">{{ formatMoney(oneToOnePrice) }}</span>
          <span class="text-xs text-muted">/wk</span>
        </p>
      </div>
    </div>
  </Link>

  <!-- ============================ LIST ============================ -->
  <Link
    v-else
    :href="href"
    class="group flex gap-5 rounded-xl border border-line bg-surface p-5 shadow-xs transition duration-200 ease-brand hover:border-line-brand hover:shadow-md"
  >
    <div class="shrink-0">
      <img
        v-if="tutor.avatar"
        :src="tutor.avatar"
        :alt="tutor.name"
        loading="lazy"
        class="size-18 rounded-full border-2 border-line object-cover"
      />
      <span
        v-else
        class="grid size-18 place-items-center rounded-full bg-brand-soft font-display text-xl font-bold text-brand"
        aria-hidden="true"
      >{{ initials }}</span>
    </div>

    <div class="min-w-0 flex-1">
      <div class="flex flex-wrap items-start justify-between gap-3">
        <div class="min-w-0">
          <h3 class="flex items-center gap-2 font-display text-lg font-bold leading-tight text-ink">
            {{ tutor.name }}
            <BadgeCheck v-if="tutor.is_verified" class="size-4 shrink-0 text-brand" aria-label="Verified" />
          </h3>
          <p v-if="institution" class="mt-0.5 text-sm font-semibold text-muted">{{ institution }}</p>
        </div>
        <p v-if="oneToOnePrice" class="text-right">
          <span class="block text-[11px] text-subtle">1:1 lesson</span>
          <span class="font-display text-xl font-bold text-ink">{{ formatMoney(oneToOnePrice) }}</span>
          <span class="text-xs text-muted">/wk</span>
        </p>
      </div>

      <div class="mt-3 flex flex-wrap gap-1.5">
        <span
          v-for="subject in displaySubjects"
          :key="subject.id"
          class="rounded-sm border border-line-brand bg-brand-soft px-2.5 py-1 text-xs font-semibold text-brand-soft-text"
        >{{ subject.name }}</span>
        <span
          v-if="hiddenSubjectsCount"
          class="rounded-sm border border-line bg-surface-2 px-2.5 py-1 text-xs font-semibold text-muted"
        >+{{ hiddenSubjectsCount }}</span>
        <span
          v-for="level in displayLevels"
          :key="`level-${level.id}`"
          class="rounded-sm border border-line px-2 py-1 text-[11px] font-semibold text-muted"
        >{{ level.name }}</span>
      </div>

      <p v-if="tutor.bio" class="mt-3 line-clamp-2 text-sm leading-relaxed text-muted">{{ tutor.bio }}</p>
    </div>
  </Link>
</template>
